import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../ui/table';
import { formatDate } from '../../utils/formatters';
import { newsletterAPI } from '../../utils/api';

interface Subscriber {
  _id: string;
  email: string;
  name?: string;
  createdAt: string;
  isActive: boolean;
}

const AdminNewsletter: React.FC = () => {
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailContent, setEmailContent] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showComposeForm, setShowComposeForm] = useState(false);
  const [selectedSubscribers, setSelectedSubscribers] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const fetchSubscribers = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        const response = await newsletterAPI.getSubscribers();
        setSubscribers(response.data);
        
        setIsLoading(false);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Der opstod en fejl ved indlæsning af abonnenter.');
        setIsLoading(false);
      }
    };
    
    fetchSubscribers();
  }, []);

  const handleToggleSubscriber = (id: string) => {
    if (selectedSubscribers.includes(id)) {
      setSelectedSubscribers(selectedSubscribers.filter(subId => subId !== id));
    } else {
      setSelectedSubscribers([...selectedSubscribers, id]);
    }
  };

  const handleToggleSelectAll = () => {
    if (selectAll) {
      setSelectedSubscribers([]);
    } else {
      setSelectedSubscribers(subscribers.filter(sub => sub.isActive).map(sub => sub._id));
    }
    setSelectAll(!selectAll);
  };

  const handleComposeNewsletter = () => {
    setShowComposeForm(true);
  };

  const handleCancelCompose = () => {
    setShowComposeForm(false);
    setEmailSubject('');
    setEmailContent('');
  };

  const handleSendNewsletter = async () => {
    if (!emailSubject.trim() || !emailContent.trim()) {
      alert('Emne og indhold er påkrævet.');
      return;
    }
    
    if (selectedSubscribers.length === 0) {
      alert('Vælg mindst én abonnent.');
      return;
    }
    
    try {
      setIsSending(true);
      
      // Call the API to send the newsletter
      await newsletterAPI.sendNewsletter(
        emailSubject,
        emailContent,
        selectedSubscribers
      );
      
      // Success
      setIsSending(false);
      setShowComposeForm(false);
      setEmailSubject('');
      setEmailContent('');
      setSelectedSubscribers([]);
      setSelectAll(false);
      
      alert('Nyhedsbrev sendt!');
    } catch (err: any) {
      setIsSending(false);
      alert(err.response?.data?.message || 'Der opstod en fejl ved afsendelse af nyhedsbrevet.');
    }
  };

  const handleToggleSubscriberStatus = async (id: string, currentStatus: boolean) => {
    try {
      await newsletterAPI.updateSubscriberStatus(id, !currentStatus);
      
      // Update local state
      setSubscribers(prevSubscribers => 
        prevSubscribers.map(sub => 
          sub._id === id ? { ...sub, isActive: !currentStatus } : sub
        )
      );
      
      // If deactivating a selected subscriber, remove from selection
      if (currentStatus && selectedSubscribers.includes(id)) {
        setSelectedSubscribers(selectedSubscribers.filter(subId => subId !== id));
      }
    } catch (err) {
      alert('Der opstod en fejl ved opdatering af abonnentens status.');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-snudefryd-blue"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  if (showComposeForm) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold text-gray-800">
            Opret nyhedsbrev
          </h3>
          <Button variant="ghost" onClick={handleCancelCompose}>
            Annuller
          </Button>
        </div>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
              Emne
            </label>
            <input
              type="text"
              id="subject"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
              placeholder="Skriv emne her..."
            />
          </div>
          
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
              Indhold
            </label>
            <textarea
              id="content"
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              rows={10}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
              placeholder="Skriv indhold her..."
            />
          </div>
          
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">
              Modtagere ({selectedSubscribers.length} valgt)
            </h4>
            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2">
              {subscribers
                .filter(sub => sub.isActive)
                .map(subscriber => (
                  <div key={subscriber._id} className="flex items-center py-1">
                    <input
                      type="checkbox"
                      id={`subscriber-${subscriber._id}`}
                      checked={selectedSubscribers.includes(subscriber._id)}
                      onChange={() => handleToggleSubscriber(subscriber._id)}
                      className="h-4 w-4 text-snudefryd-blue focus:ring-snudefryd-blue border-gray-300 rounded"
                    />
                    <label htmlFor={`subscriber-${subscriber._id}`} className="ml-2 block text-sm text-gray-700">
                      {subscriber.name ? `${subscriber.name} (${subscriber.email})` : subscriber.email}
                    </label>
                  </div>
                ))}
            </div>
          </div>
          
          <div className="pt-4">
            <Button 
              onClick={handleSendNewsletter} 
              disabled={isSending}
              className="w-full"
            >
              {isSending ? 'Sender...' : 'Send nyhedsbrev'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Nyhedsbrev</h2>
          <p className="text-gray-600 mt-1">
            Administrer abonnenter og send nyhedsbreve
          </p>
        </div>
        <Button onClick={handleComposeNewsletter}>Opret nyhedsbrev</Button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center">
            <input
              type="checkbox"
              id="select-all"
              checked={selectAll}
              onChange={handleToggleSelectAll}
              className="h-4 w-4 text-snudefryd-blue focus:ring-snudefryd-blue border-gray-300 rounded"
            />
            <label htmlFor="select-all" className="ml-2 block text-sm text-gray-700">
              Vælg alle aktive abonnenter
            </label>
            <span className="ml-2 text-sm text-gray-500">
              ({subscribers.filter(sub => sub.isActive).length} aktive)
            </span>
          </div>
        </div>
        
        {/* Mobile view - card layout */}
        <div className="md:hidden">
          <div className="space-y-4 p-4">
            {subscribers.map((subscriber) => (
              <div key={subscriber._id} className="border rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium">{subscriber.email}</div>
                    {subscriber.name && (
                      <div className="text-sm text-gray-600">{subscriber.name}</div>
                    )}
                  </div>
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      subscriber.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {subscriber.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
                
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Tilmeldt:</span> {formatDate(subscriber.createdAt)}
                </div>
                
                <div className="flex items-center justify-between pt-2">
                  {subscriber.isActive && (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedSubscribers.includes(subscriber._id)}
                        onChange={() => handleToggleSubscriber(subscriber._id)}
                        className="h-4 w-4 text-snudefryd-blue focus:ring-snudefryd-blue border-gray-300 rounded"
                        id={`mobile-sub-${subscriber._id}`}
                      />
                      <label htmlFor={`mobile-sub-${subscriber._id}`} className="ml-2 text-sm">
                        Vælg
                      </label>
                    </div>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleToggleSubscriberStatus(subscriber._id, subscriber.isActive)}
                  >
                    {subscriber.isActive ? 'Deaktiver' : 'Aktiver'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Desktop view - table layout */}
        <div className="hidden md:block">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Navn</TableHead>
                <TableHead>Tilmeldt dato</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Handlinger</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {subscribers.map((subscriber) => (
                <TableRow key={subscriber._id}>
                  <TableCell>
                    {subscriber.isActive && (
                      <input
                        type="checkbox"
                        checked={selectedSubscribers.includes(subscriber._id)}
                        onChange={() => handleToggleSubscriber(subscriber._id)}
                        className="h-4 w-4 text-snudefryd-blue focus:ring-snudefryd-blue border-gray-300 rounded"
                      />
                    )}
                  </TableCell>
                  <TableCell className="font-medium">{subscriber.email}</TableCell>
                  <TableCell>{subscriber.name || '-'}</TableCell>
                  <TableCell>{formatDate(subscriber.createdAt)}</TableCell>
                  <TableCell>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${
                        subscriber.isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {subscriber.isActive ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleSubscriberStatus(subscriber._id, subscriber.isActive)}
                    >
                      {subscriber.isActive ? 'Deaktiver' : 'Aktiver'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default AdminNewsletter; 